
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axios, useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export default function Accounts() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', phone: '', email: '', role: 'user' });
  const [adding, setAdding] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null);

  const fetchUsers = async () => {
    try { const { data } = await axios.get('/users'); setUsers(data); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleAdd = async () => {
    if (!newUser.name.trim()) { toast.error('Name is required'); return; }
    if (newUser.role === 'user' && !newUser.phone) { toast.error('Phone is required for enumerators'); return; }
    if (newUser.role === 'admin' && !newUser.email) { toast.error('Email is required for admin accounts'); return; }
    setAdding(true);
    try {
      const { data } = await axios.post('/users', newUser);
      setCreatedInfo(data);
      await fetchUsers();
      setNewUser({ name: '', phone: '', email: '', role: 'user' });
      setShowAdd(false);
      toast.success(`✅ ${data.role === 'admin' ? 'Admin' : 'Field worker'} account created!`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create account');
    } finally { setAdding(false); }
  };

  const toggleStatus = async (id, current) => {
    try {
      await axios.put(`/users/${id}/status`, { is_active: !current });
      setUsers(prev => prev.map(u => u.id === id ? { ...u, is_active: !current } : u));
      toast.success(`User ${!current ? '✅ enabled' : '❌ disabled'}`);
    } catch { toast.error('Failed to update status'); }
  };

  const resetPassword = async (id) => {
    if (!window.confirm('Reset password for this user?')) return;
    try {
      const { data } = await axios.put(`/users/${id}/reset-password`);
      toast.success('Password reset!');
      setCreatedInfo({ generatedPassword: data.newPassword, name: 'User' });
    } catch { toast.error('Failed to reset password'); }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`/users/${id}`);
      setUsers(prev => prev.filter(u => u.id !== id));
      toast.success('User deleted');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--saffron-bg)' }}>
      <div className="app-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: 34, height: 34, borderRadius: '50%', cursor: 'pointer' }}>←</button>
          <h1>Accounts</h1>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => { setShowAdd(true); setCreatedInfo(null); }}>➕ Add User</button>
      </div>

      <div className="page">

        {/* Created Account Info */}
        {createdInfo && (
          <div style={{ background: '#E8F5E9', border: '2px solid #4CAF50', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
            <div style={{ fontWeight: 700, color: '#2E7D32', marginBottom: '10px', fontSize: '0.95rem' }}>
              ✅ Account Created Successfully!
            </div>
            <div style={{ background: 'white', borderRadius: '8px', padding: '12px', fontSize: '0.82rem', lineHeight: 1.8 }}>
              <div><strong>Name:</strong> {createdInfo.name}</div>
              {createdInfo.loginInfo && <div><strong>Login:</strong> {createdInfo.loginInfo}</div>}
              {createdInfo.generatedPassword && (
                <div style={{ marginTop: '6px' }}>
                  <strong>Password:</strong>
                  <span style={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: 800, marginLeft: '8px', letterSpacing: '1px', background: '#F5F5F5', padding: '2px 8px', borderRadius: '4px' }}>
                    {createdInfo.generatedPassword}
                  </span>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
              {createdInfo.loginInfo && (
                <button onClick={() => { navigator.clipboard.writeText(createdInfo.loginInfo); toast.success('Copied!'); }} style={{ background: '#2E7D32', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem' }}>
                  📋 Copy Login Info
                </button>
              )}
              <button onClick={() => setCreatedInfo(null)} style={{ background: '#F5F5F5', color: '#555', border: 'none', padding: '6px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem' }}>
                ✕ Dismiss
              </button>
            </div>
            <div style={{ fontSize: '0.72rem', color: '#E53935', marginTop: '8px', fontWeight: 600 }}>
              ⚠️ Share this login info immediately — it won't be shown again!
            </div>
          </div>
        )}

        {/* Login Info Banner */}
        <div style={{ background: 'var(--saffron-pale)', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', fontSize: '0.8rem', color: 'var(--saffron-deep)' }}>
          <strong>🔐 Login System:</strong><br/>
          👤 <strong>Enumerators</strong> → Login with <em>Name + Phone Number</em> (no password)<br/>
          🛡️ <strong>Admins</strong> → Login with <em>Email + Password</em>
        </div>

        {/* Users List */}
        {loading ? <div className="loader"><div className="spinner"/></div> :
          users.map(user => (
            <div key={user.id} className="card" style={{ padding: '14px 16px', marginBottom: '10px', opacity: user.is_active ? 1 : 0.55 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                    background: user.role === 'admin' ? '#E3F2FD' : 'var(--saffron-pale)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: '1.1rem', color: user.role === 'admin' ? '#1565C0' : 'var(--saffron-deep)'
                  }}>
                    {user.name.charAt(0)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>{user.name}</div>
                    {user.phone && <div style={{ fontSize: '0.75rem', color: '#757575' }}>📱 {user.phone}</div>}
                    {user.email && <div style={{ fontSize: '0.75rem', color: '#757575' }}>✉️ {user.email}</div>}
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap', alignItems:'center' }}>
                      <span className="tag">{user.role === 'user' ? '👤 Enumerator' : '🛡️ Admin'}</span>
                      {user.role === 'user' && user.enumerator_id && (
                        <span style={{ background:'linear-gradient(135deg,#E55100,#FF6B00)', color:'white', padding:'2px 9px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:700, letterSpacing:'0.5px' }}>
                          {user.enumerator_id}
                        </span>
                      )}
                      <span style={{ background: user.is_active ? '#E8F5E9' : '#FFEBEE', color: user.is_active ? '#2E7D32' : '#C62828', padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600 }}>
                        {user.is_active ? '● Active' : '○ Disabled'}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                  <button onClick={() => toggleStatus(user.id, user.is_active)}
                    style={{ background: user.is_active ? '#FFEBEE' : '#E8F5E9', color: user.is_active ? '#C62828' : '#2E7D32', border: 'none', borderRadius: '8px', padding: '6px 8px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700 }}>
                    {user.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button onClick={() => resetPassword(user.id)}
                    style={{ background: '#FFF9C4', color: '#F57F17', border: 'none', borderRadius: '8px', padding: '6px 8px', cursor: 'pointer', fontSize: '0.8rem' }}>
                    🔑
                  </button>
                  <button onClick={() => deleteUser(user.id, user.name)}
                    style={{ background: '#FFEBEE', color: '#C62828', border: 'none', borderRadius: '8px', padding: '6px 8px', cursor: 'pointer', fontSize: '0.8rem' }}>
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))
        }

        {users.length === 0 && !loading && (
          <div className="empty-state"><h3>No accounts yet. Add your first enumerator!</h3></div>
        )}
      </div>

      {/* Add User Modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div className="section-title" style={{ marginBottom: 0 }}>➕ Add New Account</div>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer' }}>✕</button>
            </div>

            {isSuperAdmin && (
              <div className="form-group">
                <label className="form-label">Account Type</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['user', 'admin'].map(r => (
                    <label key={r} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px', padding: '10px', borderRadius: '10px', border: `2px solid ${newUser.role === r ? 'var(--saffron)' : 'var(--border)'}`, cursor: 'pointer', background: newUser.role === r ? 'var(--saffron-pale)' : 'white' }}>
                      <input type="radio" name="role" value={r} checked={newUser.role === r} onChange={() => setNewUser(p => ({ ...p, role: r }))} style={{ display: 'none' }} />
                      <span style={{ fontSize: '1.1rem' }}>{r === 'user' ? '👤' : '🛡️'}</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>{r === 'user' ? 'Enumerator' : 'Admin'}</div>
                        <div style={{ fontSize: '0.68rem', color: '#9E9E9E' }}>{r === 'user' ? 'Name + Phone' : 'Email + Password'}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="form-input" placeholder="Full name" value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} />
            </div>

            {(newUser.role === 'user' || !isSuperAdmin) && (
              <div className="form-group">
                <label className="form-label">Phone Number * <span style={{ color: '#9E9E9E', fontWeight: 400, textTransform: 'none' }}>(used for login)</span></label>
                <input className="form-input" type="tel" placeholder="10-digit phone" maxLength={10} value={newUser.phone} onChange={e => setNewUser(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} />
              </div>
            )}

            {newUser.role === 'admin' && isSuperAdmin && (
              <div className="form-group">
                <label className="form-label">Email Address * <span style={{ color: '#9E9E9E', fontWeight: 400, textTransform: 'none' }}>(used for login)</span></label>
                <input className="form-input" type="email" placeholder="admin@email.com" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} />
              </div>
            )}

            <div style={{ background: 'var(--saffron-pale)', padding: '10px 12px', borderRadius: '8px', fontSize: '0.78rem', color: 'var(--saffron-deep)', marginBottom: '16px', lineHeight: 1.6 }}>
              {newUser.role === 'user'
                ? '👤 Field worker will login with their <strong>Name + Phone Number</strong>. No password needed.'
                : '🛡️ Admin will login with <strong>Email + Password</strong>. Password will be auto-generated.'
              }
            </div>

            <button className="btn btn-primary btn-full" onClick={handleAdd} disabled={adding}>
              {adding ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Creating...</> : '✅ Create Account'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}